import { lightSanitiseText } from '../core/utils/utils';
import TitledPanel from '../core/TitledPanel';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import TitledTextField from '../core/TitledTextField';
import AddButton from './AddButton';
import CancelButton from './CancelButton';
import LanguageSelector from './LanguageSelector';
import PrimaryButton from './PrimaryButton';
import { globalStyles as styles } from './style/gstheme';

class BasicLanguageField extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            text: "",
            lang: null
        };
    }

    handleClickOpen = () => {
        const langsToUse = this.computeLangsToUse();
        const selectedLang = this.props.viewLang || this.props.defaultLang;
        // If only one language option select it
        const initialLang = langsToUse.includes(selectedLang) ? selectedLang : (
            langsToUse.length === 1 ? langsToUse[0] : null
        );
        this.setState({
            open: true,
            lang: initialLang
        });
    };

    addLabel = () => {
        this.props.onAddLanguage(this.state.lang, this.state.text.trim());
        this.exitAddLabel();
    };

    exitAddLabel = () => {
        const langsToUse = this.computeLangsToUse();
        this.setState({ open: false, text: "", lang: this.props.defaultLang && langsToUse.includes(this.props.defaultLang) ? this.props.defaultLang : "" });
    };

    selectLanguage = (lang) => this.setState({ lang });

    handleChange = event => {
        const t = event.target;
        const cleanText = lightSanitiseText(t.value.trimLeft());
        this.setState({ [t.name]: cleanText });
    };

    computeLangsToUse() {
        const { onePerLang, existingLangs, langs } = this.props;
        let result = onePerLang && existingLangs ?
            langs.filter(lang => !existingLangs.includes(lang))
            :
            langs;
        // For uses where users can also add typed literals we need to disallow from language option and force typed literal entry
        if (this.props.enforceLanguage) {
            result = result.filter(lang => lang !== "und");
        }
        return result;
    }

    render() {

        const { existingValues, existingLangs, title, helpText, multiline, maxLength, onePerLang, icon, tooltipText,
            classes, name, defaultLang, placeholder, required, dataTest, excludeNoLanguage } = this.props;
        const { text, lang } = this.state;
        const langsToUse = this.computeLangsToUse();
        const langFields = existingValues ?
            (Array.isArray(existingValues && existingValues[lang]) ?
                existingValues[lang]
                :
                [existingValues[lang]]
            )
            : [];
        const existingOnePerLang = existingLangs && onePerLang && existingLangs.includes(lang);
        const invalid = (text !== "" && langFields.includes(text.trim())) || existingOnePerLang;

        return (
            <React.Fragment>
                <AddButton icon={icon} dataTest={dataTest + "-add"} onClick={this.handleClickOpen} title={tooltipText} />
                <Dialog
                    data-test="add-language-value-dialog"
                    maxWidth="md"
                    fullWidth
                    classes={{
                        paperWidthMd: classes.mediumDialog
                    }}
                    open={this.state.open}
                    onClose={this.exitAddLabel}
                    aria-labelledby="text-dialog-title">
                    <DialogTitle data-test="add-language-field-title" disableTypography className={classes.mainHeading} id="text-dialog-title">{title ? title : 'Add label'}</DialogTitle>
                    <DialogContent data-test="add-language-field-dialog" style={{ ...(multiline) && { height: '290px' } }}>
                        {helpText && helpText.map((line, index) =>
                            <Typography color="textSecondary" variant="body2" paragraph key={"help-" + index}>{line}</Typography>)}
                        <TitledPanel title="Language">
                            <LanguageSelector
                                includeLabel={false}
                                langs={langsToUse}
                                excludeNoLanguage={excludeNoLanguage}
                                selectedLang={lang}
                                defaultLang={defaultLang}
                                onSelect={this.selectLanguage} />
                        </TitledPanel>
                        <form autoComplete="off" noValidate="novalidate">
                            <TitledTextField
                                dataTest="language-text"
                                name="text"
                                title={name || "Text"}
                                margin={true}
                                placeholder={placeholder}
                                autoFocus={true}
                                error={invalid}
                                errorText={invalid ?
                                    existingOnePerLang ?
                                        "This language has been added by another user. Please Cancel."
                                        :
                                        "This value already exists."
                                    :
                                    null}
                                required={required}
                                text={text}
                                multiline={multiline}
                                maxLength={maxLength || 500}
                                onEnter={text.length > 0 && lang !== "" ? this.addLabel : null}
                                onChange={this.handleChange} />
                        </form>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton onClick={this.exitAddLabel} />
                        <PrimaryButton
                            dataTest="add-language-field"
                            disabled={lang === "" || text.length === 0 || text.length > maxLength || invalid}
                            onClick={this.addLabel}>Add</PrimaryButton>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

BasicLanguageField.defaultProps = {
    multiline: false,
    onePerLang: false,
    enforceLanguage: false,
    excludeNoLanguage: false,
    required: true,
    tooltipText: "Add a new language value",
    maxLength: 100
};

BasicLanguageField.propTypes = {
    dataTest: PropTypes.string,
    icon: PropTypes.object,
    maxLength: PropTypes.number,
    multiline: PropTypes.bool,
    onePerLang: PropTypes.bool,
    enforceLanguage: PropTypes.bool,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    existingLangs: PropTypes.array,
    required: PropTypes.bool,
    excludeNoLanguage: PropTypes.bool,
    langs: PropTypes.array,
    viewLang: PropTypes.string,
    defaultLang: PropTypes.string,
    existingValues: PropTypes.object,
    helpText: PropTypes.array,
    onAddLanguage: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(BasicLanguageField);